import { useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";

import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";

import { StreamContext } from "contexts/Stream";
import { DYNAMIC_BASE_URL } from "shared/enum";
import useVariant from "hooks/useVariant";
import useUpdateVariant from "hooks/useUpdateVariant";
import useVariantsFilter from "hooks/useVariantsFilter";
import useGtagVariantData from "hooks/useGtagVariantData";
import useMapToClosestVariant from "hooks/useMapToClosestVariant";
import useDistinctVariantTypes from "hooks/useDistinctVariantTypes";
import { isDarkTheme } from "helpers/darkTheme";
import { getWasmMethods } from "@3ds/3DEXCITEshow-streaming";

import type { FeatureListItem } from "types/app";
import { useMediaQuery } from "@mui/material";

const variantSorter = (a: string, b: string) => (a > b ? -1 : 1);

type VariantFiltersProps = {
  openFeaturePreview: (options: FeatureListItem, disableAnalyticsEvent?: boolean) => void;
};

export default function VariantFilters({ openFeaturePreview }: VariantFiltersProps) {
  const wasmMethods = getWasmMethods();
  const { turntableState, setTurntableState } = useContext(StreamContext);
  const variant = useVariant();
  const filteredVariants = useVariantsFilter({ code: variant?.code });
  const variantPacks = useDistinctVariantTypes("variant", filteredVariants, variantSorter);
  const updateVariant = useUpdateVariant();
  const mapData = useMapToClosestVariant(variantPacks, variant, true);
  const getGtagData = useGtagVariantData();
  const theme = useTheme();
  const isTab = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const selectedVariant = variantPacks.find(({ selected }) => selected);
  const { isTurntable } = turntableState;

  const handleChange = async (newValue: string) => {
    if (selectedVariant?.code === newValue) return;

    updateVariant({ variant: newValue }, filteredVariants, true);
    window.gtag(
      "event",
      "variant_change",
      getGtagData("model", `Change variant (Pack) to ${newValue}`),
    );

    if (isTurntable) {
      setTurntableState((prev) => ({
        ...prev,
        selectedVariants: [...prev.selectedVariants, mapData[newValue].friendly_name],
      }));

      return;
    }

    try {
      if (!isTurntable && wasmMethods && "switchVariants" in wasmMethods) {
        await wasmMethods?.switchVariants(mapData[newValue].friendly_name);
      } else {
        // for turntable
        // setDemoState((prev) => ({
        //   ...prev,
        //   variants: [...prev.variants, newColor?.friendly_name],
        // }));
      }
    } catch (ex) {
      console.log(ex, " switch variant cordis error");
    }
  };

  const handleInfoClick = (e: React.MouseEvent, code: string) => {
    e.stopPropagation();

    const infoVariant = mapData[code];

    if (!infoVariant?.overlayImg) return;

    openFeaturePreview(
      {
        title: infoVariant.variant,
        thumbnail: infoVariant.overlayImg,
        previewImg: infoVariant.overlayImg,
      },
      false,
    );
  };

  return (
    <Container isTab={isTab} my={1} rowGap={1} columnGap={2} justifyContent="space-between">
      {variantPacks.map(({ code }) => (
        <IconAndTextContainer
          key={code}
          selected={code === selectedVariant?.code}
          overlayImgPresent={!!mapData[code]?.overlayImg}
          onClick={() => handleChange(code)}
        >
          <InfoIcon className="info-icon" onClick={(e) => handleInfoClick(e, code)} />
          <VariantIcon src={DYNAMIC_BASE_URL + mapData[code].overlayImg} alt={code} />
          <TextContainer>
            <Typography variant="body2">{code}</Typography>
          </TextContainer>
        </IconAndTextContainer>
      ))}
    </Container>
  );
}
type ContainerProps = {
  isTab: boolean;
};
const Container = styled(Box)<ContainerProps>`
  display: grid;
  grid-template-columns: ${(props) => (props.isTab ? "0.7fr" : "1fr 1fr")};
`;

type IconAndTextContainerProps = {
  selected?: boolean;
  overlayImgPresent: boolean;
};

const IconAndTextContainer = styled("div")<IconAndTextContainerProps>`
  display: grid;
  width: 100%;
  height: 135px;
  border-radius: 12px;
  cursor: pointer;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme, selected }) =>
    isDarkTheme(theme.palette.mode)
      ? selected
        ? "#fff"
        : "transparent"
      : selected
        ? "#000"
        : "transparent"};
  background-color: rgba(0, 0, 0, 0.1);
  grid-template-rows: 3fr 1fr;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  & > img {
    opacity: ${({ selected }) => (selected ? 1 : 0.6)};
    filter: blur(${({ selected }) => (selected ? 0 : 0.5)}px);
  }

  & > .info-icon {
    // TODO: disable info icon button
    display: none;
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    cursor: ${({ overlayImgPresent }) => (overlayImgPresent ? "help" : "not-allowed")};
    z-index: 2;
    transition: all 0.2s ease;
    opacity: ${({ selected }) => (selected ? 0 : 0.5)};

    ${({ theme }) => theme.breakpoints.down("sm")} {
      display: none;
    }
  }

  &:hover > .info-icon {
    opacity: 0.5;
  }

  & > .info-icon:hover {
    opacity: 0.8;
  }
`;

const InfoIcon = styled(InfoTwoToneIcon)``;

const VariantIcon = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    filter: blur(0px);
  }
`;

const TextContainer = styled("div")`
  margin: auto;
  text-align: center;
`;
